import {
  GuestLiteModel,
  GuestModel,
  getGuestKey,
  getGuestsKey,
  useGetGuest,
  useGetGuests,
} from 'api-hooks/guest';
import { OnChangeParams, Option } from 'baseui/select';
import { NavigationRoutes } from 'common/constants';
import { QueryTransformer } from 'common/helpers/common';
import { capitalize, transformLabel } from 'common/utils/string';
import { Input } from 'components/elements';
import useGetCrudPermission from 'hooks/use-get-crud-permission';
import useNavigation from 'hooks/use-navigation';
import useSelectInputHelper from 'hooks/use-select-input-helper';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

export function guestOptionTransformer(
  item: GuestLiteModel | GuestModel,
): Option {
  return {
    id: item.id,
    label: `${transformLabel(
      [capitalize(item.title), item.name, item.alias],
      ' ',
    )}`,
  };
}

interface Props {
  name: string;
  required?: boolean;
  label?: string;
  placeholder?: string;
  hideRequiredStar?: boolean;
  hideLabel?: boolean;
  onClose?: () => void;
  onAfterChange?: (value?: OnChangeParams) => void;
  onAfterDetailChange?: (value: GuestModel) => void;
  multi?: boolean;
}

export default function GuestSelectInput(props: Props) {
  const { t } = useTranslation();
  const { label = t('common:guest'), placeholder = t('common:select_guest') } =
    props;
  const { control, setValue } = useFormContext<any>();
  const value = useWatch({ control, name: props.name });

  const { navigate } = useNavigation();

  const permission = useGetCrudPermission('guests');
  const { overrides, ...selectProps } = useSelectInputHelper({
    value,
    onSelectItem: (value) => {
      setValue(props.name, value[0]?.id || '', {
        shouldTouch: true,
        shouldValidate: true,
      });
    },
    useListQueryHook: useGetGuests,
    useDetailLazyQueryHook: useGetGuest,
    listKeyFunction: getGuestsKey,
    detailKeyFunction: getGuestKey,
    getMemoizedListVariables: React.useCallback(
      (page, q) => ({
        params: {
          q,
          page,
        },
      }),
      [],
    ),
    renderCreate: permission.create,
    onClickCreate: React.useCallback(() => {
      navigate(NavigationRoutes.GuestsCreate);
      props.onClose?.();
    }, [navigate, props]),
    getMemoizedDetailVariables: React.useCallback(
      (value: string) => ({
        id: value,
      }),
      [],
    ),
    listTransformer: React.useCallback((result) => {
      return result.data.map((v) => guestOptionTransformer(v));
    }, []),
    detailTransformer: React.useCallback((result) => {
      const newResult = QueryTransformer(result, GuestModel);
      props.onAfterDetailChange?.(newResult.data);
      return guestOptionTransformer(newResult.data);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []),
    paginationTransformer: React.useCallback((result) => {
      return result.meta;
    }, []),
  });

  return (
    <Input
      type="select"
      name={props.name}
      required={props.required}
      label={label}
      placeholder={placeholder}
      onAfterChange={props.onAfterChange}
      noMargin
      multi={props.multi}
      hideRequiredStar={props.hideRequiredStar}
      hideLabel={props.hideLabel}
      filterOptions={null}
      overrides={overrides}
      {...selectProps}
    />
  );
}
